import { CheckIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { memo, useEffect, useId, useState } from 'react';
import { T } from '../../../translation/src';
import { AI_PROGRESS_SLOW_HINT_AFTER_MS, aiProgressSteps, formatElapsed, sketchFraction, type AiStep, type AiStepList } from './aiProgressState';
import type { AiRunState } from './aiProgressTracker';

/**
 * Keyframes for the waiting UI. Every animation sits behind
 * prefers-reduced-motion: no-preference; without it the UI is static but
 * still updates (ticks, thought line, sketch).
 */
const STYLES = `
@keyframes aiprog-fade { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@keyframes aiprog-sweep { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
@keyframes aiprog-scan { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(44px); } }
@keyframes aiprog-march { to { stroke-dashoffset: -12; } }
@keyframes aiprog-breathe { 0%, 100% { opacity: .35; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: no-preference) {
  .aiprog-fade { animation: aiprog-fade .6s ease-out both; }
  .aiprog-sweep { animation: aiprog-sweep 1.8s ease-in-out infinite; }
  .aiprog-scan { animation: aiprog-scan 3.2s ease-in-out infinite; }
  .aiprog-march { animation: aiprog-march 1s linear infinite; }
  .aiprog-breathe { animation: aiprog-breathe 1.6s ease-in-out infinite; }
  .aiprog-draw { transition: stroke-dashoffset .8s ease-out; }
}
`;

/** What the AI is designing: a floor-plan object, or a print template (sheet layout). */
export type AiSubject = 'object' | 'template';

/**
 * The sketches drawn in as the design streams in, and the faint marching
 * outline of what they fill: a generic floor-plan symbol (a sofa-like
 * footprint), or a sheet (border, plan view, title block, scale bar, north arrow).
 */
const SKETCHES: Record<AiSubject, { outline: { x: number; y: number; width: number; height: number }; paths: readonly string[] }> = {
  object: {
    outline: { x: 14, y: 14, width: 92, height: 44 },
    paths: ['M14 14 H106 V58 H14 Z', 'M14 14 V58 M24 14 V58', 'M96 14 V58 M106 14 V58', 'M24 24 H96', 'M47.3 24 V58 M72.6 24 V58', 'M30 64 H90', 'M30 61 V67 M90 61 V67'],
  },
  template: {
    outline: { x: 16, y: 8, width: 88, height: 56 },
    paths: [
      'M20 12 H100 V60 H20 Z',
      'M24 16 H96 V44 H24 Z',
      'M36 21 H62 V39 H36 Z M49 21 V39 M36 30 H49',
      'M68 48 H96 V57 H68 Z',
      'M68 52.5 H96 M79 48 V57 M88 52.5 V57',
      'M26 53 H44 M26 51.5 V54.5 M35 52 V54 M44 51.5 V54.5',
      'M54 55 L57.5 47 L61 55 L57.5 53 Z',
    ],
  },
};

/** The blueprint graphic: a grid, a scan line while thinking, lines drawn in as `fraction` grows. */
const BlueprintSketch: React.FC<{ fraction: number; thinking: boolean; done: boolean; subject: AiSubject }> = ({ fraction, thinking, done, subject }) => {
  const { outline, paths } = SKETCHES[subject];
  const drawn = fraction * paths.length;
  const gridId = `aiprog-grid-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <svg viewBox="0 0 120 72" className="h-16 w-28 shrink-0 overflow-visible" aria-hidden="true">
      <defs>
        <pattern id={gridId} width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M8 0 H0 V8" fill="none" stroke="currentColor" strokeWidth="0.4" className="opacity-20" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="120" height="72" rx="6" className="fill-french-violet-600/5 dark:fill-neon-green-300/5" />
      <rect x="0" y="0" width="120" height="72" rx="6" fill={`url(#${gridId})`} className="text-french-violet-600 dark:text-neon-green-300" />
      {/* The footprint (or page) to be, as a faint marching outline. */}
      <rect {...outline} fill="none" strokeWidth="0.8" strokeDasharray="3 3" className={classNames('stroke-gray-400 dark:stroke-gray-500', !done && 'aiprog-march')} />
      {paths.map((d, i) => {
        const part = Math.min(1, Math.max(0, drawn - i));
        return <path key={d} d={d} pathLength={1} strokeDasharray="1 1" strokeDashoffset={1 - part} fill="none" strokeWidth="1.6" strokeLinecap="round" className="aiprog-draw stroke-french-violet-600 dark:stroke-neon-green-300" />;
      })}
      {thinking && <line x1="10" y1="12" x2="110" y2="12" strokeWidth="1" className="aiprog-scan stroke-french-violet-600/50 dark:stroke-neon-green-300/50" />}
    </svg>
  );
};

const StepLabel: React.FC<{ step: AiStep; list: AiStepList; subject: AiSubject }> = ({ step, list, subject }) => {
  switch (step.id) {
    case 'understand':
      return <T _str="understanding the request" swc />;
    case 'design':
      return (
        <>
          <T _str="designing" swc />
          <span className="text-gray-500 dark:text-gray-400">
            {' '}
            (<T _str="thinking" />)
          </span>
        </>
      );
    case 'draw':
      return subject === 'template' ? <T _str="laying out the sheet" swc /> : <T _str="drawing the object" swc />;
    case 'check':
      return <T _str="checking it" swc />;
    case 'fix':
      return (
        <>
          {list.issues === 1 ? <T _str="fixing 1 problem" swc /> : list.issues > 1 ? <T _str="fixing {count} problems" args={{ count: list.issues }} swc /> : <T _str="trying again" swc />}
          <span className="text-gray-500 dark:text-gray-400">
            {' '}
            (<T _str="attempt {attempt} of {max}" args={{ attempt: list.attempt, max: list.maxAttempts }} />
            {step.status === 'current' && list.fixPhase === 'redrawing' && (
              <>
                , <T _str="redrawing" />
              </>
            )}
            {step.status === 'current' && list.fixPhase === 'rechecking' && (
              <>
                , <T _str="checking again" />
              </>
            )}
            )
          </span>
        </>
      );
    case 'done':
      return <T _str="done" swc />;
  }
};

const StepMarker: React.FC<{ status: AiStep['status']; live: boolean }> = ({ status, live }) => {
  if (status === 'done') {
    return (
      <span className={classNames('flex h-4 w-4 items-center justify-center rounded-full', live ? 'bg-neon-green-500 text-black' : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-200')}>
        <CheckIcon className="h-3 w-3" />
      </span>
    );
  }
  if (status === 'current') {
    return (
      <span className={classNames('relative flex h-4 w-4 items-center justify-center rounded-full border-2', live ? 'border-french-violet-600 dark:border-neon-green-300' : 'border-dashed border-gray-400')}>
        <span className={classNames('h-1.5 w-1.5 rounded-full', live ? 'aiprog-breathe bg-french-violet-600 dark:bg-neon-green-300' : 'bg-gray-400')} />
      </span>
    );
  }
  return <span className="h-4 w-4 rounded-full border border-gray-300 dark:border-gray-600" />;
};

/** Re-renders every second while `running` (the elapsed clock and time-based estimates). */
function useNow(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return now;
}

interface AiProgressProps {
  run: AiRunState;
  className?: string;
  /** Leave out the latest-thought line (the chat shows every thought itself). */
  hideThought?: boolean;
  /** What is being designed (wording and sketch); an object by default. */
  subject?: AiSubject;
}

/**
 * What the AI is doing while it designs an object or a template: the steps (ticked when
 * done, the current one animated), its latest thought, the name
 * once written, a sketch that fills in as the design streams in, and the
 * elapsed time. Live from the server's progress events; without them, an
 * estimate that says so.
 */
const AiProgress_: React.FC<AiProgressProps> = ({ run, className, hideThought, subject = 'object' }) => {
  const now = useNow(run.running);
  if (!run.running || run.startedAt === null) return null;
  const elapsed = Math.max(0, now - run.startedAt);
  const { progress } = run;
  const list = aiProgressSteps(progress, elapsed);
  const stage = progress.event?.stage;
  const thinking = !stage || stage === 'thinking' || stage === 'fixing';

  return (
    <div className={classNames('flex flex-col gap-3 rounded-xl border border-gray-200 bg-[#f4f5f5] p-3 text-sm dark:border-gray-700 dark:bg-[#262626]', className)}>
      <style>{STYLES}</style>
      <div className="flex items-center gap-3">
        <BlueprintSketch fraction={sketchFraction(progress)} thinking={thinking} done={stage === 'done'} subject={subject} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="truncate font-medium" title={progress.name ?? undefined}>
            {progress.name ? (
              <span key={progress.name} className="aiprog-fade">
                {progress.name}
              </span>
            ) : (
              subject === 'template' ? <T _str="designing your template" swc /> : <T _str="designing your object" swc />
            )}
          </div>
          <div className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {formatElapsed(elapsed)}
            {list.attempt > 1 && (
              <>
                {' · '}
                <T _str="attempt {attempt} of {max}" args={{ attempt: list.attempt, max: list.maxAttempts }} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Announced politely: the steps only (the clock and the thought line change too often). */}
      <ol role="status" aria-live="polite" className="flex flex-col gap-1">
        {list.steps.map((step) => (
          <li key={step.id} className={classNames('relative flex items-center gap-2 overflow-hidden rounded-md px-1.5 py-0.5', step.status === 'pending' && 'text-gray-400 dark:text-gray-500', step.status === 'current' && 'font-medium')}>
            {step.status === 'current' && list.live && (
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 text-french-violet-600 dark:text-neon-green-300">
                <span className="aiprog-sweep absolute inset-0 bg-[linear-gradient(90deg,transparent,currentColor,transparent)] opacity-10" />
              </span>
            )}
            <StepMarker status={step.status} live={list.live} />
            <span className="relative">
              <StepLabel step={step} list={list} subject={subject} />
            </span>
          </li>
        ))}
      </ol>

      {progress.thought && !hideThought && (
        <p key={progress.thought} className="aiprog-fade line-clamp-2 border-l-2 border-french-violet-600/40 pl-2 text-xs italic text-gray-600 dark:border-neon-green-300/40 dark:text-gray-300">
          {progress.thought}
        </p>
      )}

      {!list.live && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <T _str="no live updates from the server: the steps above are a rough estimate" swc />
        </p>
      )}
      {elapsed >= AI_PROGRESS_SLOW_HINT_AFTER_MS && (
        <p className="aiprog-fade text-xs text-gray-500 dark:text-gray-400">
          {subject === 'template' ? <T _str="still working: detailed sheets can take a couple of minutes" swc /> : <T _str="still working: complex objects can take a couple of minutes" swc />}
        </p>
      )}
    </div>
  );
};

export const AiProgress = memo(AiProgress_);
